import type { LessonAiStandardsRequest } from "../../auth/api-client";

export type MentionKind = "standard" | "glossary";

export interface MentionToken {
  readonly kind: MentionKind;
  readonly id: string;
  readonly label: string;
  readonly description: string;
}

export type MentionSegment =
  | { readonly type: "text"; readonly value: string }
  | { readonly type: "mention"; readonly token: MentionToken };

export interface MentionPromptDoc {
  readonly segments: readonly MentionSegment[];
}

export function emptyMentionDoc(): MentionPromptDoc {
  return { segments: [{ type: "text", value: "" }] };
}

export function mentionDocFromPlainText(text: string): MentionPromptDoc {
  return { segments: [{ type: "text", value: text }] };
}

export function isMentionDocEmpty(doc: MentionPromptDoc): boolean {
  const hasMention = doc.segments.some((s) => s.type === "mention");
  const text = doc.segments
    .filter((s): s is { type: "text"; value: string } => s.type === "text")
    .map((s) => s.value)
    .join("");
  return !hasMention && !text.trim();
}

/** Prompt string sent to lesson/course AI (short inline hints for pills). */
export function toAiPromptText(doc: MentionPromptDoc): string {
  return doc.segments
    .map((s) => {
      if (s.type === "text") return s.value;
      const prefix = s.token.kind === "standard" ? "Standard" : "Glossary";
      return `[${prefix} ${s.token.label}]`;
    })
    .join("")
    .trim();
}

export function toLessonAiStandardsRequest(
  doc: MentionPromptDoc,
  standardSetId: string | undefined
): LessonAiStandardsRequest | undefined {
  if (!standardSetId) return undefined;
  const selectedStandardIds: string[] = [];
  const selectedGlossaryIds: string[] = [];
  for (const s of doc.segments) {
    if (s.type !== "mention") continue;
    if (s.token.kind === "standard") selectedStandardIds.push(s.token.id);
    else selectedGlossaryIds.push(s.token.id);
  }
  return { standardSetId, selectedStandardIds, selectedGlossaryIds };
}

/** Merge AI-suggested codes/terms into doc as new mention pills (no duplicates). */
export function applyAiMentionsToDoc(
  doc: MentionPromptDoc,
  standards: { id: string; code: string; description: string }[],
  glossary: { id: string; term: string; definition: string }[],
  standardCodes: string[],
  glossaryTerms: string[]
): MentionPromptDoc {
  const existing = new Set(
    doc.segments
      .filter((s): s is { type: "mention"; token: MentionToken } => s.type === "mention")
      .map((s) => `${s.token.kind}:${s.token.id}`)
  );
  const codeSet = new Set(standardCodes.map((c) => c.trim().toLowerCase()).filter(Boolean));
  const termSet = new Set(glossaryTerms.map((t) => t.trim().toLowerCase()).filter(Boolean));
  const toAdd: MentionToken[] = [];
  for (const s of standards) {
    if (!codeSet.has(s.code.trim().toLowerCase())) continue;
    const key = `standard:${s.id}`;
    if (existing.has(key)) continue;
    toAdd.push({ kind: "standard", id: s.id, label: s.code, description: s.description });
  }
  for (const g of glossary) {
    if (!termSet.has(g.term.trim().toLowerCase())) continue;
    const key = `glossary:${g.id}`;
    if (existing.has(key)) continue;
    toAdd.push({ kind: "glossary", id: g.id, label: g.term, description: g.definition });
  }
  if (toAdd.length === 0) return doc;
  const segments = [...doc.segments];
  for (const token of toAdd) {
    segments.push({ type: "mention", token });
    segments.push({ type: "text", value: " " });
  }
  return { segments };
}
